import { loadStripe } from '@stripe/stripe-js/pure';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import AddAdditionalFilesDialog from '../components/additionalFiles/AddAdditionalFilesDialog';
import ChangeStudyTitle from '../components/changeStudyTitle/ChangeStudyTitle';
import PersonalizedVideoFilesRadiologyReport from '../components/personalizedVideoFiles/PersonalizedVideoFilesRadiologyReportDialog';
import UploadProgress from '../containers/upload/UploadProgress';
import { allCaseUploadOptions } from '../models/Case';
import { DCM } from '../models/Resource';
import User from '../models/User';
import parseFiles from '../utils/dicomUtils';
import PaymentUtils from '../utils/payment.utils';

const defaultStatus = {
  fileType: '',
  files: [],
  formOnlineFolderLink: {
    onlineServiceName: '',
    sharingLink: '',
    studyDescription: '',
  },
  formOnlinePortalAccess: {
    portalAddress: '',
    portalPassword: '',
    portalUsername: '',
    studyName: '',
  },
  hasReport: false,
  imageCount: 0,
  imageUploadOption: allCaseUploadOptions.imageUpload.value,
  reportUploaded: false,
  selectedPackage: 'standard',
  selectedPaymentMethod: 'patient-pay',
  study: null,
  uploaded: false,
  uploadedReport: [],
};

const REPORT_FORMATS = ['pdf', 'jpg', 'jpeg', 'tiff', 'png', 'txt', 'rtf', 'doc', 'docx'];

export default class CreateCaseBloc {
  patientData$ = new BehaviorSubject({});
  filesStatus$ = new BehaviorSubject(defaultStatus);
  isSubmitting$ = new BehaviorSubject(false);
  additionalFiles$ = new BehaviorSubject([]);
  termsCheck$ = new BehaviorSubject(false);
  adminList$ = new BehaviorSubject([]);
  selectedAdminList$ = new BehaviorSubject([]);
  notifyMe$ = new BehaviorSubject('yes');
  discountData$ = new BehaviorSubject({});
  redirectToCaseCreated$ = new BehaviorSubject(false);

  constructor({
    adminRepository,
    authRepository,
    patientRepository,
    userRepository,
    casesRepository,
    paymentRepository,
    dialogRepository,
    errorRepository,
    uploadProgressRepository,
    workspaceRepository,
  }) {
    this.authRepository = authRepository;
    this.casesRepository = casesRepository;
    this.patientRepository = patientRepository;
    this.paymentRepository = paymentRepository;
    this.dialogRepository = dialogRepository;
    this.errorRepository = errorRepository;
    this.uploadProgressRepository = uploadProgressRepository;
    this.adminRepository = adminRepository;
    this.userRepository = userRepository;
    this.workspaceRepository = workspaceRepository;

    this.caseSub = this.casesRepository.caseToComplete$.subscribe((caseStudy) => this.initializeCaseData(caseStudy));

    this.authRepository.getWorkspace();
    this.getWorkspaceAdmins();
  }

  get isMediphanyWorkspace$() {
    return this.authRepository.userWorkspace$.pipe(map((workspace) => workspace.isMediphanyWorkspace));
  }

  get userDetails$() {
    return this.userRepository.userDetails$;
  }

  get userWorkspace$() {
    return this.authRepository.userWorkspace$;
  }

  async initStripe() {
    if (!this.stripe) {
      this.stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISH_KEY, {
        apiVersion: '2020-08-27',
      });
    }
  }

  dispose() {
    this.caseSub.unsubscribe();
    this.casesRepository.caseToComplete$.next(null);
  }

  openUploadReportDialog() {
    this.dialogRepository.loadDialog(PersonalizedVideoFilesRadiologyReport, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onReportUpload: (files) => {
        this.uploadReports(files);
        this.dialogRepository.toggleDialog(false);
      },
    });
  }

  async parseFiles(files, type) {
    if (type === DCM) {
      try {
        const status = await parseFiles(files, this.filesStatus$.getValue());

        if (status) {
          if (status.uploaded) status.fileType = DCM;
          this.filesStatus$.next(status);
        }
      } catch (error) {
        this.errorRepository.error$.next({
          message: error.message,
          severity: 'error',
        });
      }
      return;
    }

    const fileType = type === 'document' ? 'pdf' : type;
    const filesStatus = this.filesStatus$.getValue();
    this.filesStatus$.next({
      ...filesStatus,
      fileType,
      files: files || [],
      hasReport: false,
      imageCount: 1,
      study: files?.[0]?.name || '',
      uploaded: true,
    });
  }

  updateTermsCheck(target, value) {
    if (target === 'terms') this.termsCheck$.next(value);
  }

  removeFiles() {
    this.filesStatus$.next({
      ...this.filesStatus$.getValue(),
      fileType: '',
      files: [],
      imageCount: 0,
      uploaded: false,
    });
  }

  removeReport() {
    this.filesStatus$.next({
      ...this.filesStatus$.getValue(),
      reportUploaded: false,
      uploadedReport: null,
    });
  }

  async getWorkspaceAdmins() {
    const currentUser = this.authRepository.auth$.getValue();

    const response = await this.adminRepository.getActiveAdminInWorkspace();
    const admins = response?.data || [];

    const filteredAdmins = admins.filter((admin) => admin.email !== currentUser.email);
    this.adminList$.next(filteredAdmins);
  }

  async handleGetWorkspaceDiscountDetail() {
    const result = await this.workspaceRepository.discountDetail();
    this.discountData$.next(result?.discount);
  }

  updateSelectedAdminList(data) {
    this.selectedAdminList$.next(data);
  }

  updateNotifyMe(data) {
    this.notifyMe$.next(data);
  }

  updateImageUploadOption(value) {
    this.filesStatus$.next({
      ...this.filesStatus$.getValue(),
      imageUploadOption: value,
    });
  }

  setFormOnlinePortalAccess(formOnlinePortalAccess) {
    this.filesStatus$.next({
      ...this.filesStatus$.getValue(),
      formOnlinePortalAccess,
    });
  }

  setFormOnlineFolderLink(formOnlineFolderLink) {
    this.filesStatus$.next({
      ...this.filesStatus$.getValue(),
      formOnlineFolderLink,
    });
  }

  setSelectedPaymentMethod(selectedPaymentMethod) {
    this.filesStatus$.next({
      ...this.filesStatus$.getValue(),
      selectedPaymentMethod,
    });
  }

  setSelectedPackage(selectedPackage) {
    this.filesStatus$.next({
      ...this.filesStatus$.getValue(),
      selectedPackage,
    });
  }

  updateFormOnlinePortalAccess(value, field, validForm = true) {
    const formOnlinePortalAccess = {
      ...this.filesStatus$.getValue().formOnlinePortalAccess,
      [field]: value,
      validForm,
    };

    this.setFormOnlinePortalAccess(formOnlinePortalAccess);
  }

  updateFormOnlineFolderLink(value, field, validForm = true) {
    const formOnlineFolderLink = {
      ...this.filesStatus$.getValue().formOnlineFolderLink,
      [field]: value,
      validForm,
    };

    this.setFormOnlineFolderLink(formOnlineFolderLink);
  }

  uploadReports(files) {
    const fileArray = Array.from(files);
    const reports = [];

    for (let idx = 0; idx < fileArray?.length; idx += 1) {
      const file = fileArray[idx];

      if (file) {
        const parts = file.name.split('.');
        const extension = parts[parts.length - 1];
        if (!REPORT_FORMATS.includes(extension.toLowerCase())) {
          return this.errorRepository.error$.next({
            message: 'Uploaded report must be a pdf/doc/txt/image file',
            severity: 'error',
          });
        }
        reports.push(file);
      }
    }

    if (reports.length) {
      return this.filesStatus$.next({
        ...this.filesStatus$.getValue(),
        reportUploaded: true,
        uploadedReport: reports,
      });
    }

    return null;
  }

  async getPatientByEmail(email) {
    const patient = await this.patientRepository.getPatientByEmail(email);
    if (patient) {
      this.patientData$.next(new User(patient));
    }
  }

  async sendCase(patientData, mode = 'create') {
    try {
      const { imageUploadOption } = this.filesStatus$.getValue();
      if (imageUploadOption === allCaseUploadOptions.onlinePortal.value) {
        await this.sendUploadSharedCase(patientData, mode);
        return;
      }

      if (imageUploadOption === allCaseUploadOptions.shareFolderLink.value) {
        await this.sendFolderLinkCase(patientData, mode);
        return;
      }

      if (imageUploadOption === allCaseUploadOptions.mailToPOBox.value) {
        await this.sendMailCase(patientData);
        return;
      }

      await this.sendImageUploadCase(patientData, mode);
    } catch (error) {
      this.isSubmitting$.next(false);
      throw error;
    }
  }

  notifySubmitErrors(error) {
    this.errorRepository.notifyException(error);
  }

  async sendImageUploadCase(patientData, mode) {
    try {
      const { files, uploadedReport, caseId, imageUploadOption, fileType } = this.filesStatus$.getValue();

      if (imageUploadOption !== allCaseUploadOptions.imageUpload.value) {
        return;
      }

      const adminIds = this.getSelectedAdminIds();
      const fileStatus = this.filesStatus$.getValue();

      this.isSubmitting$.next(true);

      this.uploadProgressRepository.start();
      this.dialogRepository.loadDialog(UploadProgress, { preventClose: true });

      const payload = {
        additionalFiles: this.additionalFiles$.getValue(),
        caseId,
        files,
        mainFileType: fileType,
        notifiedAdmins: adminIds,
        questionText: patientData.questionText || patientData.caseInformation,
        selectedPaymentMethod: fileStatus.selectedPaymentMethod,
        selectedPaymentPackage: fileStatus.selectedPackage,
        uploadedReport,
        workspaceId: patientData.workspaceId,
      };

      this.addPatientData(patientData, payload);

      if (mode === 'create') {
        const result = await this.casesRepository.submitCase(payload, this.uploadProgressRepository.progress$);
        this.errorRepository.error$.next({
          message: 'Your files are being processed. Once this is completed you will be notified by email',
          severity: 'success',
        });
        await this.handleCreateCaseCheckoutSection(result);
      } else {
        const result = await this.casesRepository.completeCase(payload, this.uploadProgressRepository.progress$);
        await this.handleCreateCaseCheckoutSection(result);
      }

      this.uploadProgressRepository.finish();
      this.isSubmitting$.next(false);
    } catch (error) {
      this.isSubmitting$.next(false);
      throw error;
    }
  }

  getSelectedAdminIds() {
    const adminIds = [];
    const notifyMe = this.notifyMe$.getValue();

    if (notifyMe === 'yes') {
      const user = this.authRepository.auth$.getValue();
      if (user?.id) adminIds.push(user.id);
    }

    const selectedAdmins = this.selectedAdminList$.getValue();
    if (selectedAdmins.length) {
      const ids = selectedAdmins.map((admin) => admin.id);
      if (ids) adminIds.push(...(ids || []));
    }

    return adminIds;
  }

  addPatientData(patientData, payload) {
    if (patientData.employer) {
      /* eslint-disable no-param-reassign */
      payload.claimant = {
        argument: patientData.argument,
        dateOfBirth: patientData.dateOfBirth,
        employer: patientData.employer,
        gender: patientData.gender,
        name: patientData.name,
        preliminaryDate: patientData.preliminaryDate,
      };
    } else {
      payload.patient = {
        dateOfBirth: patientData.dateOfBirth,
        email: patientData.email,
        gender: patientData.gender,
        name: patientData.name,
        phoneNumber: patientData.phoneNumber,
        preferredName: patientData.preferredName,
        userId: patientData.userId,
      };
      /* eslint-disable no-param-reassign */
    }
  }

  async sendUploadSharedCase(patientData, mode) {
    const { files, uploadedReport, caseId, imageUploadOption, formOnlinePortalAccess } = this.filesStatus$.getValue();
    if (imageUploadOption !== allCaseUploadOptions.onlinePortal.value) return;

    const adminIds = this.getSelectedAdminIds();
    const fileStatus = this.filesStatus$.getValue();

    this.isSubmitting$.next(true);

    this.uploadProgressRepository.start();
    this.dialogRepository.loadDialog(UploadProgress, { preventClose: true });

    const payload = {
      additionalFiles: this.additionalFiles$.getValue(),
      caseId,
      files,
      isLawFirm: patientData.isLawFirm,
      notifiedAdmins: adminIds,
      questionText: patientData.questionText || patientData.caseInformation,
      selectedPaymentMethod: fileStatus.selectedPaymentMethod,
      selectedPaymentPackage: fileStatus.selectedPackage,
      uploadSharedInfo: {
        portalAddress: formOnlinePortalAccess.portalAddress,
        portalPassword: formOnlinePortalAccess.portalPassword,
        portalUsername: formOnlinePortalAccess.portalUsername,
        studyName: formOnlinePortalAccess.studyName,
      },
      uploadedReport,
      workspaceId: patientData.workspaceId,
    };

    this.addPatientData(patientData, payload);

    try {
      if (mode === 'create') {
        const result = await this.casesRepository.submitCase(payload, this.uploadProgressRepository.progress$);

        this.errorRepository.error$.next({
          message: 'Your request have been created',
          severity: 'success',
        });

        await this.handleCreateCaseCheckoutSection(result);
      } else {
        const result = await this.casesRepository.completeUploadSharedReportForPatient(payload);
        await this.handleCreateCaseCheckoutSection(result);
      }

      this.uploadProgressRepository.finish();
      this.isSubmitting$.next(false);
    } catch (error) {
      this.uploadProgressRepository.fail();
      this.isSubmitting$.next(false);
    }
  }

  async sendFolderLinkCase(patientData, mode) {
    const { files, uploadedReport, caseId, imageUploadOption, formOnlineFolderLink } = this.filesStatus$.getValue();
    if (imageUploadOption !== allCaseUploadOptions.shareFolderLink.value) return;

    const adminIds = this.getSelectedAdminIds();
    const fileStatus = this.filesStatus$.getValue();
    this.isSubmitting$.next(true);

    this.uploadProgressRepository.start();
    this.dialogRepository.loadDialog(UploadProgress, { preventClose: true });

    const payload = {
      additionalFiles: this.additionalFiles$.getValue(),
      caseId,
      caseLinkInfo: {
        onlineServiceName: formOnlineFolderLink.onlineServiceName,
        sharingLink: formOnlineFolderLink.sharingLink,
        studyDescription: formOnlineFolderLink.studyDescription,
      },
      files,
      isLawFirm: patientData.isLawFirm,
      notifiedAdmins: adminIds,
      questionText: patientData.questionText || patientData.caseInformation,
      selectedPaymentMethod: fileStatus.selectedPaymentMethod,
      selectedPaymentPackage: fileStatus.selectedPackage,
      uploadedReport,
      workspaceId: patientData.workspaceId,
    };

    this.addPatientData(patientData, payload);

    try {
      this.isSubmitting$.next(true);

      if (mode === 'create') {
        const result = await this.casesRepository.submitCase(payload, this.uploadProgressRepository.progress$);

        this.errorRepository.error$.next({
          message: 'Your request have been created',
          severity: 'success',
        });

        await this.handleCreateCaseCheckoutSection(result);
      } else {
        const result = await this.casesRepository.completeUploadSharedReportForPatient(payload);
        await this.handleCreateCaseCheckoutSection(result);
      }

      this.uploadProgressRepository.finish();
      this.isSubmitting$.next(false);
    } catch (error) {
      this.uploadProgressRepository.fail();
      this.isSubmitting$.next(false);
      throw error;
    }
  }

  async sendMailCase(patientData) {
    const { files, uploadedReport, imageUploadOption } = this.filesStatus$.getValue();
    if (imageUploadOption !== allCaseUploadOptions.mailToPOBox.value) return;

    const adminIds = this.getSelectedAdminIds();
    const fileStatus = this.filesStatus$.getValue();

    const payload = {
      additionalFiles: this.additionalFiles$.getValue(),
      files,
      isMailCase: true,
      notifiedAdmins: adminIds,
      questionText: patientData.caseInformation,
      selectedPaymentMethod: fileStatus.selectedPaymentMethod,
      selectedPaymentPackage: fileStatus.selectedPackage,
      uploadedReport,
      workspaceId: patientData.workspaceId,
    };

    this.addPatientData(patientData, payload);

    try {
      this.isSubmitting$.next(true);
      await this.casesRepository.submitCase(payload);

      this.errorRepository.error$.next({
        message: 'Your request have been created',
        severity: 'success',
      });

      this.uploadProgressRepository.finish();
      this.isSubmitting$.next(false);
    } catch (error) {
      this.uploadProgressRepository.fail();
      this.isSubmitting$.next(false);
      throw error;
    }
  }

  resetData() {
    this.filesStatus$.next({ ...defaultStatus });
    this.patientData$.next({});
  }

  openChangeTitle() {
    this.dialogRepository.loadDialog(ChangeStudyTitle, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onSave: (value) => {
        this.setStudy(value);
        this.dialogRepository.toggleDialog(false);
      },
      study: this.filesStatus$.getValue().study,
    });
  }

  setStudy(study) {
    this.filesStatus$.next({
      ...this.filesStatus$.getValue(),
      study,
    });
  }

  initializeCaseData(caseStudy) {
    if (caseStudy) {
      if (caseStudy.patient) {
        this.patientData$.next(new User(caseStudy.patient));
      }
      const currentStatus = this.filesStatus$.getValue();
      if (caseStudy.dicomStudy) {
        currentStatus.uploaded = true;
        currentStatus.study = caseStudy.dicomStudy.title;
        currentStatus.imageCount = caseStudy.dicomStudy.imageCount;
      }
      currentStatus.hasReport = caseStudy.reports?.length;
      currentStatus.caseId = caseStudy.id;
      this.filesStatus$.next({
        ...currentStatus,
      });
    }
  }

  openSelectImageUploadDialogSelector() {
    this.dialogRepository.loadDialog(AddAdditionalFilesDialog, {
      dialogTitle: 'Select Files',
      onCrossIconClick: () => this.closeDialog(),
      onUpload: (data) => {
        this.closeDialog();
        this.parseFiles(data.files, data.type);
      },
    });
  }

  openAdditionalFileDialog() {
    this.dialogRepository.loadDialog(AddAdditionalFilesDialog, {
      onCrossIconClick: () => this.closeDialog(),
      onUpload: (data) => {
        this.closeDialog();
        this.addAdditionalFiles(data);
      },
    });
  }

  closeDialog() {
    this.dialogRepository.toggleDialog(false);
  }

  async handleCreateCaseCheckoutSection(caseData) {
    const fileStatus = this.filesStatus$.getValue();

    const discountData = this.discountData$.getValue();
    // eslint-disable-next-line camelcase
    if (discountData?.id && discountData?.percent_off === 100) {
      this.redirectToCaseCreated$.next(true);
      return;
    }

    if (fileStatus.selectedPaymentMethod !== 'admin-pay') return;

    let selectedPackage = PaymentUtils.PACKAGE_STANDARD_VIDEO_REPORT.plan;
    if (fileStatus.selectedPackage === 'express') selectedPackage = PaymentUtils.PACKAGE_EXPRESS_VIDEO_REPORT.plan;

    const { sessionId } = await this.paymentRepository.createCaseCheckoutSession(null, '/cases', selectedPackage.name, caseData.id, caseData?.patient?.id, 'case-created');
    const result = await this.redirectToCheckout(sessionId);
    if (result.error) this.errorRepository.error$.next(result.error);
  }

  async redirectToCheckout(sessionId) {
    return this.paymentRepository.redirectToCheckout(sessionId);
  }

  addAdditionalFiles(data) {
    const { files, type } = data;
    if (!files) return;

    const addittionalFiles = this.additionalFiles$.getValue();

    this.additionalFiles$.next([...addittionalFiles, { files, key: `'create-case-additional-files-${addittionalFiles.length}'`, type }]);
  }

  removeAdditionalFilesByIndex(index) {
    const additionalFiles = this.additionalFiles$.getValue().filter((_, fIndex) => fIndex !== index);
    this.additionalFiles$.next(additionalFiles);
  }
}
